import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { exerciseCatalog as staticCatalog } from '../data/exerciseCatalog';
import { appPathname } from '../utils/appPath';

// Types
interface Exercise {
  name: string;
  equipment?: string;
  type?: string;
  isCustom?: boolean;
  muscleGroup?: string;
}

type ExerciseCatalog = Record<string, Exercise[]>;

interface ExerciseContextValue {
  catalog: ExerciseCatalog;
  loading: boolean;
  error: string | null;
  lastFetched: Date | null;
  getExercisesForMuscle: (muscle: string) => Exercise[];
  getMuscleGroups: () => string[];
  getTotalCount: () => number;
  searchExercises: (query: string) => Exercise[];
  refresh: () => Promise<void>;
}

interface ExerciseProviderProps {
  children: ReactNode;
}

const ExerciseContext = createContext<ExerciseContextValue | null>(null);

const API_URL = '';
const CACHE_KEY = 'gym_notebook_exercise_catalog';
const CACHE_EXPIRY_KEY = 'gym_notebook_exercise_catalog_expiry';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

export function ExerciseProvider({ children }: ExerciseProviderProps) {
  const [catalog, setCatalog] = useState<ExerciseCatalog>(staticCatalog as ExerciseCatalog);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const retriedRef = useRef(false);
  const fetchExercisesRef = useRef<((showLoading?: boolean) => Promise<void>) | null>(null);

  const fetchExercises = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);
    
    try {
      console.log('[Exercises] Fetching from API...');
      const response = await fetch(`${API_URL}/api/exercises`);
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.catalog && Object.keys(data.catalog).length > 0) {
        setCatalog(data.catalog);
        setLastFetched(new Date());
        
        localStorage.setItem(CACHE_KEY, JSON.stringify(data.catalog));
        localStorage.setItem(CACHE_EXPIRY_KEY, (Date.now() + CACHE_DURATION).toString());
        
        console.log('[Exercises] Fetched from API:', data.count, 'exercises in', Object.keys(data.catalog).length, 'groups');
      } else {
        console.log('[Exercises] API returned empty, using static catalog');
      }
    } catch (e: any) {
      console.error('[Exercises] Fetch error:', e);
      setError(e.message);
      // Iter310 — the very first fetch after boot can be aborted (wrapped
      // window.fetch / service-worker race). Retry once instead of living on
      // the bundled static catalog until the next page load.
      if (!retriedRef.current) {
        retriedRef.current = true;
        setTimeout(() => { fetchExercisesRef.current?.(false); }, 1500);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  fetchExercisesRef.current = fetchExercises;

  // Load from cache on mount
  useEffect(() => {
    const loadFromCache = (): boolean => {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        const expiry = localStorage.getItem(CACHE_EXPIRY_KEY);
        
        if (cached && expiry) {
          const expiryTime = parseInt(expiry, 10);
          if (Date.now() < expiryTime) {
            const parsed = JSON.parse(cached);
            if (parsed && Object.keys(parsed).length > 0) {
              setCatalog(parsed);
              setLastFetched(new Date(expiryTime - CACHE_DURATION));
              console.log('[Exercises] Loaded from cache:', Object.keys(parsed).length, 'muscle groups');
              return true;
            }
          }
        }
      } catch (e) {
        console.error('[Exercises] Cache load error:', e);
      }
      return false;
    };

    const hasCache = loadFromCache();
    // Defer the 519 KB /api/exercises fetch off the critical path so it
    // doesn't compete for bandwidth with the LCP image / main bundle.
    // The static catalog (bundled) is good enough for first paint, and
    // the API enrichment lands in the background after the page is
    // interactive. Skip the network round-trip entirely on the public
    // landing page (no auth) — that user will never hit the workout view
    // until they sign in, and we'd re-fetch fresh data at that point
    // anyway. This applies even without a cache (first-time visitor)
    // so PSI's cold Slow-4G run doesn't get 512 KB on the critical path.
    const isPublicLanding = (
      typeof window !== 'undefined' &&
      (appPathname() === '/' || appPathname() === '/login') &&
      !localStorage.getItem('gym_notebook_token')
    );
    if (isPublicLanding) {
      setLoading(false);
      return;
    }
    const schedule: (cb: () => void) => void =
      (typeof window !== 'undefined' && (window as any).requestIdleCallback)
        ? (cb) => (window as any).requestIdleCallback(cb, { timeout: 5000 })
        : (cb) => setTimeout(cb, 1500);
    schedule(() => { void fetchExercises(!hasCache); });
  }, [fetchExercises]);

  const getExercisesForMuscle = useCallback((muscle: string): Exercise[] => {
    return catalog[muscle] || [];
  }, [catalog]);

  const getMuscleGroups = useCallback((): string[] => {
    return Object.keys(catalog);
  }, [catalog]);

  const getTotalCount = useCallback((): number => {
    return Object.values(catalog).reduce((total, exercises) => total + exercises.length, 0);
  }, [catalog]);

  const searchExercises = useCallback((query: string): Exercise[] => {
    if (!query || query.length < 2) return [];
    
    const lowerQuery = query.toLowerCase();
    const results: Exercise[] = [];
    
    Object.entries(catalog).forEach(([muscle, exercises]) => {
      exercises.forEach(ex => {
        if (ex.name.toLowerCase().includes(lowerQuery)) {
          results.push({ ...ex, muscleGroup: muscle });
        }
      });
    });
    
    return results;
  }, [catalog]);

  const refresh = useCallback(() => {
    return fetchExercises(true);
  }, [fetchExercises]);

  const value: ExerciseContextValue = {
    catalog,
    loading,
    error,
    lastFetched,
    getExercisesForMuscle,
    getMuscleGroups,
    getTotalCount,
    searchExercises,
    refresh,
  };

  return (
    <ExerciseContext.Provider value={value}>
      {children}
    </ExerciseContext.Provider>
  );
}

export function useExercises(): ExerciseContextValue {
  const context = useContext(ExerciseContext);
  if (!context) {
    throw new Error('useExercises must be used within an ExerciseProvider');
  }
  return context;
}

// Legacy function for backward compatibility
export const getMergedCatalog = (): ExerciseCatalog => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const apiCatalog = JSON.parse(cached);
      if (apiCatalog && Object.keys(apiCatalog).length > 0) {
        return mergeWithCustomExercises(apiCatalog);
      }
    }
  } catch (e) {
    console.error('[Exercises] Error reading cache for legacy function:', e);
  }
  
  return mergeWithCustomExercises(staticCatalog as ExerciseCatalog);
};

// Helper to merge with user's custom exercises from localStorage
const mergeWithCustomExercises = (baseCatalog: ExerciseCatalog): ExerciseCatalog => {
  const merged: ExerciseCatalog = { ...baseCatalog };
  
  Object.keys(merged).forEach(key => {
    merged[key] = [...merged[key]];
  });
  
  try {
    const savedCustom = localStorage.getItem('gym_notebook_custom_exercises');
    if (savedCustom) {
      const customExercises = JSON.parse(savedCustom);
      if (Array.isArray(customExercises)) {
        customExercises.forEach((ex: any) => {
          if (ex.muscleGroup && merged[ex.muscleGroup]) {
            merged[ex.muscleGroup].push({
              name: ex.name,
              equipment: ex.equipment,
              type: ex.type || 'compound',
              isCustom: true
            });
          }
        });
      }
    }
  } catch (e) {
    console.error('[Exercises] Error loading custom exercises:', e);
  }
  
  return merged;
};

export default ExerciseContext;
